import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetModelApplicationErrors,
  GetModelResponse,
  ListModelsApplicationErrors,
  ListModelsResponse,
  QueryModelDataApplicationErrors,
  QueryModelDataOptions,
  QueryModelDataResponse,
  getModel as universalGetModel,
  listModels as universalListModels,
  queryModelData as universalQueryModelData,
} from './analytics-model-v3-model-analytics-model.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/analytics-model' };

export function queryModelData(
  httpClient: HttpClient
): QueryModelDataSignature {
  return (
    modelId: string,
    options?: NonNullablePaths<QueryModelDataOptions, `fields` | `interval`, 2>
  ) =>
    universalQueryModelData(
      modelId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryModelDataSignature {
  /**
   * Retrieves data from a semantic model, given the provided fields, filters, sorting, and paging.
   *
   *
   * Query Model Data runs with these defaults, which you can override:
   *
   * - `paging.limit` is `50`
   * - `paging.offset` is `0`
   *
   * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
   * @param - ID or slug of the model to query. Call `List Models` to retrieve available model IDs.
   * @returns Response for querying data from a semantic model.
   */
  (
    modelId: string,
    options?: NonNullablePaths<QueryModelDataOptions, `fields` | `interval`, 2>
  ): Promise<
    NonNullablePaths<
      QueryModelDataResponse,
      `results` | `cacheStatus.status`,
      3
    > & {
      __applicationErrorsType?: QueryModelDataApplicationErrors;
    }
  >;
}

export function getModel(httpClient: HttpClient): GetModelSignature {
  return (modelId: string) =>
    universalGetModel(
      modelId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetModelSignature {
  /**
   * Retrieves a semantic model by ID or slug.
   * @param - ID or slug of the model to retrieve. Call `List Models` to retrieve available model IDs.
   * @returns Response for retrieving a semantic model.
   */
  (modelId: string): Promise<
    NonNullablePaths<
      GetModelResponse,
      | `model._id`
      | `model.slug`
      | `model.version`
      | `model.measures`
      | `model.measures.${number}.name`
      | `model.measures.${number}.type`
      | `model.dimensions`
      | `model.parameters`
      | `model.keywords`,
      5
    > & {
      __applicationErrorsType?: GetModelApplicationErrors;
    }
  >;
}

export function listModels(httpClient: HttpClient): ListModelsSignature {
  return () =>
    universalListModels(
      // @ts-ignore
      { httpClient }
    );
}

interface ListModelsSignature {
  /**
   * Retrieves a list of all available semantic models.
   * @returns Response for listing all available semantic models.
   */
  (): Promise<
    NonNullablePaths<
      ListModelsResponse,
      | `models`
      | `models.${number}._id`
      | `models.${number}.slug`
      | `models.${number}.version`,
      4
    > & {
      __applicationErrorsType?: ListModelsApplicationErrors;
    }
  >;
}

export {
  CacheControlEnum,
  CacheSatusEnum,
  Control,
  DateTimeInterval,
  FieldFilter,
  FieldVisibilityEnum,
  GetModelRequest,
  GetModelResponse,
  ListModelsRequest,
  ListModelsResponse,
  Model,
  ModelField,
  ModelFieldDataRow,
  ModelFieldFilters,
  ModelFieldTypeEnum,
  ModelFieldValue,
  ModelFieldValueValueOneOf,
  ModelFilterConditionEnum,
  ModelFilterPrefixEnum,
  Paging,
  PagingMetadata,
  QueryModelDataOptions,
  QueryModelDataRequest,
  QueryModelDataResponse,
  SemanticModel,
  SortOrder,
  Sorting,
  Status,
} from './analytics-model-v3-model-analytics-model.universal.js';
